// get_service_availability_bands for the page of days the availability panel
// shows: one call per page (a week: p_days <= 7), cached, debounced on selection
// changes, stale answers dropped.
//
// The answer belongs to its question: state carries the key it answers, and a
// key that has no answer yet reads as "loading" — never the previous
// selection's days (the repo idiom: unit-pricing.ts answeredKey === quoteKey).
// Only a timeout or a real error becomes "error"; an answer that was
// superseded (cleanup bumped the sequence) is dropped silently.
//
// Fail open: an error is never an empty ready (which would read "Full"); the
// panel shows "Not checked" and unchecked times, and the hold decides.

import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "../config";
import {
  addDaysYmd,
  bandRowsCarryNotice,
  groupBandRows,
  type DayBands,
  type SelectionEntry,
} from "./availability-logic";

export type BandsState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; days: Record<string, DayBands>; noticeKnown: boolean; at: number }
  | { status: "error" };

const TTL_MS = 60_000;
const CACHE_MAX = 12;
export const PAGE_TIMEOUT_MS = 8_000;
const RECHECK_TIMEOUT_MS = 5_000;
const STALE_ON_VISIBLE_MS = 30_000;

type CacheEntry = { at: number; days: Record<string, DayBands>; noticeKnown: boolean };

function daysFrom(fromYmd: string, n: number): string[] {
  const out: string[] = [];
  for (let i = 0; i < n; i++) out.push(addDaysYmd(fromYmd, i));
  return out;
}

/** One bands read; useAloneBands asks it per single option too. */
export async function fetchBands(
  serviceId: string,
  selection: SelectionEntry[],
  fromYmd: string,
  days: number,
  timeoutMs: number,
  outer?: AbortSignal,
): Promise<{ ok: true; days: Record<string, DayBands>; noticeKnown: boolean } | { ok: false; aborted: boolean }> {
  const ac = new AbortController();
  let timedOut = false;
  const to = setTimeout(() => {
    timedOut = true;
    ac.abort();
  }, timeoutMs);
  const onOuter = () => ac.abort();
  outer?.addEventListener("abort", onOuter);
  try {
    const { data, error } = await supabase
      .rpc("get_service_availability_bands", {
        p_service_id: serviceId,
        p_selection: selection,
        p_from: fromYmd,
        p_days: days,
      })
      .abortSignal(ac.signal);
    if (error) return { ok: false, aborted: !timedOut && !!outer?.aborted };
    const grouped = groupBandRows(data, daysFrom(fromYmd, days));
    if (!grouped) return { ok: false, aborted: false };
    return { ok: true, days: grouped, noticeKnown: bandRowsCarryNotice(data) };
  } catch {
    return { ok: false, aborted: !timedOut && !!outer?.aborted };
  } finally {
    clearTimeout(to);
    outer?.removeEventListener("abort", onOuter);
  }
}

export function useAvailabilityBands(a: {
  serviceId: string;
  selection: SelectionEntry[] | null;
  fromYmd: string | null;
  days: number;
  cartSig: string;
  debounceMs?: number;
}): {
  state: BandsState;
  /** The question `state` answers (changes with selection, page and cart). */
  key: string;
  /** A fresh one-day read that bypasses the cache and merges into the page. */
  recheckDay: (day: string) => Promise<DayBands | "error">;
  /** Drop the cached page and ask again now. */
  refetch: () => void;
} {
  const { serviceId, selection, fromYmd, days, cartSig } = a;
  const debounceMs = a.debounceMs ?? 300;
  const selSig = selection ? JSON.stringify(selection) : "";
  const key = [serviceId, selSig, fromYmd ?? "", days, cartSig].join("§");

  const [answer, setAnswer] = useState<{ key: string; state: BandsState }>({ key: "", state: { status: "idle" } });
  const [nonce, setNonce] = useState(0);
  const cacheRef = useRef(new Map<string, CacheEntry>());
  const seqRef = useRef(0);
  const prevSelSigRef = useRef<string | null>(null);
  const forceRef = useRef(false);
  const keyRef = useRef(key);
  keyRef.current = key;
  const inputsRef = useRef({ serviceId, selection, fromYmd, days });
  inputsRef.current = { serviceId, selection, fromYmd, days };

  useEffect(() => {
    const my = ++seqRef.current;
    const sel = inputsRef.current.selection;
    if (!sel || !fromYmd || days < 1) {
      prevSelSigRef.current = selSig;
      setAnswer({ key, state: { status: "idle" } });
      return () => {
        seqRef.current++;
      };
    }
    const force = forceRef.current;
    forceRef.current = false;
    const cache = cacheRef.current;
    const hit = cache.get(key);
    if (hit && !force && Date.now() - hit.at < TTL_MS) {
      prevSelSigRef.current = selSig;
      setAnswer({ key, state: { status: "ready", days: hit.days, noticeKnown: hit.noticeKnown, at: hit.at } });
      return () => {
        seqRef.current++;
      };
    }
    setAnswer({ key, state: { status: "loading" } });
    // Debounce only a changed selection (a stepper being pressed); a new page
    // or a refetch asks at once.
    const wait = !force && prevSelSigRef.current !== null && prevSelSigRef.current !== selSig ? debounceMs : 0;
    prevSelSigRef.current = selSig;
    const ac = new AbortController();
    const timer = setTimeout(async () => {
      if (my !== seqRef.current) return;
      const r = await fetchBands(serviceId, sel, fromYmd, days, PAGE_TIMEOUT_MS, ac.signal);
      if (my !== seqRef.current) return; // superseded: cleanup already ran
      if (!r.ok) {
        if (r.aborted) return;
        setAnswer({ key, state: { status: "error" } });
        return;
      }
      const at = Date.now();
      cache.delete(key);
      cache.set(key, { at, days: r.days, noticeKnown: r.noticeKnown });
      while (cache.size > CACHE_MAX) cache.delete(cache.keys().next().value as string);
      setAnswer({ key, state: { status: "ready", days: r.days, noticeKnown: r.noticeKnown, at } });
    }, wait);
    return () => {
      seqRef.current++;
      clearTimeout(timer);
      ac.abort();
    };
    // selection travels via inputsRef; `key` carries its signature.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, nonce]);

  const refetch = useCallback(() => {
    cacheRef.current.delete(keyRef.current);
    forceRef.current = true;
    setNonce((n) => n + 1);
  }, []);

  const recheckDay = useCallback(async (day: string): Promise<DayBands | "error"> => {
    const { serviceId: sid, selection: sel } = inputsRef.current;
    const askedKey = keyRef.current;
    if (!sel) return "error";
    const r = await fetchBands(sid, sel, day, 1, RECHECK_TIMEOUT_MS);
    if (!r.ok) return "error";
    const fresh = r.days[day] ?? { day, bands: [] };
    // Merge into the page it belongs to — only if the question is unchanged.
    if (keyRef.current === askedKey) {
      const entry = cacheRef.current.get(askedKey);
      if (entry && entry.days[day]) {
        const merged = { ...entry.days, [day]: fresh };
        const at = Date.now();
        cacheRef.current.set(askedKey, { at, days: merged, noticeKnown: entry.noticeKnown && r.noticeKnown });
        setAnswer((cur) =>
          cur.key === askedKey && cur.state.status === "ready"
            ? { key: askedKey, state: { status: "ready", days: merged, noticeKnown: entry.noticeKnown && r.noticeKnown, at } }
            : cur,
        );
      }
    }
    return fresh;
  }, []);

  // A tab left in the background comes back to a fresh page.
  const readyAt = answer.key === key && answer.state.status === "ready" ? answer.state.at : null;
  useEffect(() => {
    if (readyAt == null) return;
    const onVisible = () => {
      if (document.visibilityState === "visible" && Date.now() - readyAt > STALE_ON_VISIBLE_MS) refetch();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [readyAt, refetch]);

  const state: BandsState =
    answer.key === key ? answer.state : selection && fromYmd && days >= 1 ? { status: "loading" } : { status: "idle" };
  return { state, key, recheckDay, refetch };
}
